import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Container, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  colorPublicMenuAccent,
  colorPublicMenuBg,
  colorPublicMenuChipBorder,
  colorPublicMenuDivider,
  colorPublicMenuText,
  colorPublicMenuTextMuted,
} from "src/design/tokens";

export interface MenuCategory {
  id: string;
  label: string;
}

interface MenuCategoryNavProps {
  categories: MenuCategory[];
}

// Height of the sticky header the nav parks beneath; also used to offset
// scroll-to so a section title clears the header + nav.
const STICKY_OFFSET = 64;
const ACTIVE_THRESHOLD = STICKY_OFFSET + 80;

// Sticky category navigation rendered as a horizontal carousel: when there are
// more sections than fit, prev/next arrows page the tab strip and a scroll spy
// keeps the active tab highlighted and in view. Selecting a tab scrolls its
// section into view.
export const MenuCategoryNav = ({ categories }: MenuCategoryNavProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeId, setActiveId] = useState(categories[0]?.id ?? "");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    setCanScrollLeft(node.scrollLeft > 1);
    setCanScrollRight(node.scrollLeft + node.clientWidth < node.scrollWidth - 1);
  }, []);

  // Track overflow so the arrows only appear when the strip can scroll.
  useEffect(() => {
    updateArrows();
    const node = scrollerRef.current;
    node?.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      node?.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, categories]);

  // Scroll spy: highlight the section currently under the header.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        let current = categories[0]?.id ?? "";
        for (const category of categories) {
          const el = document.getElementById(category.id);
          if (el && el.getBoundingClientRect().top <= ACTIVE_THRESHOLD) {
            current = category.id;
          }
        }
        setActiveId(current);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [categories]);

  // Keep the active tab visible within the strip as the spy moves it.
  useEffect(() => {
    const index = categories.findIndex((c) => c.id === activeId);
    tabRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeId, categories]);

  const pageStrip = (direction: 1 | -1) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * node.clientWidth * 0.7, behavior: "smooth" });
  };

  const handleSelect = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const arrowSx = {
    flexShrink: 0,
    color: colorPublicMenuText,
    border: `1px solid ${colorPublicMenuChipBorder}`,
    width: 32,
    height: 32,
    "&:hover": { backgroundColor: `${colorPublicMenuText}14` },
    "&.Mui-disabled": { opacity: 0, pointerEvents: "none" as const },
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: STICKY_OFFSET,
        zIndex: 1100,
        backgroundColor: colorPublicMenuBg,
        borderBottom: `1px solid ${colorPublicMenuDivider}`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
          <IconButton
            aria-label="scroll categories left"
            size="small"
            onClick={() => pageStrip(-1)}
            disabled={!canScrollLeft}
            sx={arrowSx}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <Box
            ref={scrollerRef}
            sx={{
              display: "flex",
              gap: { xs: 2.5, md: 4 },
              overflowX: "auto",
              flex: 1,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {categories.map((category, index) => {
              const isActive = category.id === activeId;
              return (
                <Box
                  key={category.id}
                  ref={(el: HTMLDivElement | null) => (tabRefs.current[index] = el)}
                  onClick={() => handleSelect(category.id)}
                  sx={{
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontSize: { xs: 12, md: 13 },
                    fontWeight: 600,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: isActive ? colorPublicMenuText : colorPublicMenuTextMuted,
                    borderBottom: `2px solid ${
                      isActive ? colorPublicMenuAccent : "transparent"
                    }`,
                    pb: 0.5,
                    transition: "color 0.2s ease",
                    "&:hover": { color: colorPublicMenuText },
                  }}
                >
                  {category.label}
                </Box>
              );
            })}
          </Box>

          <IconButton
            aria-label="scroll categories right"
            size="small"
            onClick={() => pageStrip(1)}
            disabled={!canScrollRight}
            sx={arrowSx}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
};
